import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { metricKeys } from '@pulse/metrics';
import { DATASET, INTERNAL_UNKNOWN } from '@utils/constants';
import { filterSelector, selectedKPIConfigSelector } from '@store/overviewDashboard/base/selectors';
import { getDimensionData } from '@src/api/dimensionContainer';
import { activeTabSelector } from '@store/overviewDashboard/widgetMessageData/selectors';
import { selectedBarIntervalSelector } from '@store/overviewDashboard/impactedViewers/selectors';
import { ViewersTabKey } from '@store/overviewDashboard/widgetMessageData/reducer';
import { viewerSessionMetricList } from './utils';

const defaultSort = { key: metricKeys.BadSession, order: 'desc' };

export const query = {
  type: 'group-by',
  dataset: DATASET,
  metrics: viewerSessionMetricList,
  interval: '2018-12-19T22:35:18.907Z/2018-12-20T22:35:18.907Z',
  granularity: 'ALL',
  filter: [],
  limit: 100,
  groupBy: ['VIEWER_ID'],
  orderBy: 'desc',
  sortBy: [{ metric: metricKeys.BadSession, order: 'desc' }],
  options: {
    withTotals: false,
    withOverviewThresholds: true,
    kpiConfig: {},
  },
};

function buildQuery({ filter, interval, kpiConfig, sortBy }) {
  const { key, order } = sortBy;

  const q = cloneDeep(query);
  q.filter = filter;
  q.interval = `${interval.from}/${interval.to}`;
  q.options.kpiConfig = kpiConfig;
  q.sortBy = [{ metric: key, order }];
  return q;
}

function useFetchData() {
  const activeTab = useSelector(activeTabSelector);
  const appliedFilter = useSelector(filterSelector);
  const kpiConfig = useSelector(selectedKPIConfigSelector);
  const selectedBarInterval = useSelector(selectedBarIntervalSelector);

  const [sortBy, setSortBy] = useState(defaultSort);

  const filter = appliedFilter.rule;
  const query = buildQuery({ filter, interval: selectedBarInterval, kpiConfig, sortBy });

  const onColumnSort = ({ key }) => {
    setSortBy({ key, order: 'desc' });
  };

  const { isIdle, status, data, refetch } = useQuery(
    ['ovb-viewer-sessions', filter, selectedBarInterval, kpiConfig, sortBy],
    () =>
      getDimensionData({
        queries: [query],
      }),
    {
      // default cacheTime is 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
      enabled: activeTab === ViewersTabKey,
      select: (response) => {
        const rows = get(response, ['data', 0, 'data', 0, 'results'], []);
        const unknownRows: any = [];
        // move unknown rows to the bottom
        const filteredRows = rows.filter((row) => {
          if (row.VIEWER_ID === INTERNAL_UNKNOWN) {
            unknownRows.push(row);
            return false;
          }
          return true;
        });

        return filteredRows.concat(unknownRows);
      },
    },
  );

  return {
    isIdle,
    status,
    rows: data,
    refetch,
    sortBy,
    onColumnSort,
  };
}

export default useFetchData;
